import React, { useState, useEffect, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Heading from 'components/common/heading/heading';
import SoftwareUpdateService from 'services/softwareUpdateService';
import SoftwareUpdateHelper from 'components/navigation/softwareUpdate/softwareUpdateHelper';
import { useHistory } from 'hooks/router';
import SiteSelectorFormControl from 'components/shared/siteSelector/siteSelectorFormControl';
import ErrorItemNotFound from 'components/app/errors/errorItemNotFound';
import { Tabs, Tab } from 'components/common/tabs/tabs';
import AppContext from 'contexts/appContext';
import Panel from 'components/common/panel/panel';
import useForm from 'hooks/form';
import moment from 'moment';
import _ from 'lodash';
import './softwareUpdateEditPage.css';

const styles = {
  numberInput: {
    display: 'inline',
    width: '70px',
    textAlign: 'center'
  }
};

export default function SoftwareUpdateEditPage(props) {
  const { appState, setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: true });

  const [softwareUpdateName, setSoftwareUpdateName] = useState('');
  const [selectedType, setSelectedType] = useState();
  const [softwareVersionId, setSoftwareVersionId] = useState();
  const [distributeDateTime, setDistributeDateTime] = useState();
  const [softwareTypes, setSoftwareTypes] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [wasSelectedSitesChanged, setWasSelectedSitesChanged] = useState(false);
  const [softwareTypeVersionsMap, setSoftwareTypeVersionsMap] = useState({});
  const [isAsapSelected, setAsapSelected] = useState(false);
  const [isOnHoldSelected, setOnHoldSelected] = useState(false);
  const [updateRateVolume, setUpdateRateVolume] = useState(2);
  const [updateRateFrequency, setUpdateRateFrequency] = useState(1);
  const [distributeOptionCode, setDistributeOptionCode] = useState(false);
  const [timeTab, setTimeTab] = useState(null);
  const [softwareSectionEnabled, setSoftwareSectionEnabled] = useState(false);
  const [sitesSectionEnabled, setSitesSectionEnabled] = useState(false);
  const [timeSectionEnabled, setTimeSectionEnabled] = useState(false);
  const [optionSectionEnabled, setOptionSectionEnabled] = useState(false);
  const [dataNotFound, setDataNotFound] = useState(false);

  const history = useHistory();
  const id = props.match.params.id;
  const autoPopulate = props.location.state ? props.location.state.from : null;

  useAsyncEffect(async () => {
    setSoftwareTypes(await SoftwareUpdateHelper.getSoftwareTypes());
    setSoftwareTypeVersionsMap(await SoftwareUpdateHelper.getSoftwareVersionsForUpdate());

    const details = await SoftwareUpdateService.API.getSoftwareUpdateDetails(id);
    if (details && details.softwareUpdate) {
      setSoftwareUpdateName(details.softwareUpdate.softwareUpdateName);
      setSelectedType(details.softwareUpdate.softwareTypeId);
      setSoftwareVersionId(details.softwareUpdate.softwareVersionId.toString());
      setSelectedSites(details.detailsList.map((item) => item.hierarchyId));
      setUpdateRateFrequency(details.softwareUpdate.updateRateFrequency);
      setUpdateRateVolume(details.softwareUpdate.updateRateVolume);
      setOnHoldSelected(details.softwareUpdate.distributeOnHold);
      setAsapSelected(details.softwareUpdate.distributeAsap);
      setDistributeDateTime(details.softwareUpdate.distributeDateTime);
      if (details.softwareUpdate.distributeOnHold) {
        setTimeTab('onhold');
        setSoftwareSectionEnabled(true);
        setSitesSectionEnabled(true);
        setOptionSectionEnabled(true);
        setTimeSectionEnabled(true);
      }
      else if (details.softwareUpdate.distributeAsap) {
        setTimeTab('asap');
      }
      else {
        setTimeTab('custom');
        if (details.softwareUpdate.distributedChildCount === 0) {
          setSoftwareSectionEnabled(true);
          setSitesSectionEnabled(true);
          setOptionSectionEnabled(true);
          setTimeSectionEnabled(true);
        }
      }
    }
    else {
      setDataNotFound(true);
    }
  }, []);

  useEffect(() => {
    setAppArea(AppContext.Areas.SoftwareUpdate, 'Update Setup');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Software Update', href: '/SoftwareUpdate' }, { name: 'Update Details', href: `/SoftwareUpdate/${id}` }, { name: 'Edit Update', selected: true }]);

    if (autoPopulate && autoPopulate.siteId) {
      setSelectedSites([autoPopulate.siteId]);
      setSelectedType(autoPopulate.softwareTypeId);
      setSoftwareVersionId(autoPopulate.versionId);
    }
  }, []);

  const handleSaveUpdateClicked = submitForm(async (cancelSubmit) => {
    await SoftwareUpdateService.API.updateSoftwareUpdate({
      softwareUpdateId: id,
      softwareUpdateName,
      softwareVersionId,
      distributeDateTime,
      siteIds: selectedSites,
      sitesChanged: wasSelectedSitesChanged,
      distributeAsap: isAsapSelected,
      distributeOnHold: isOnHoldSelected,
      updateRateVolume,
      updateRateFrequency,
      distributeOptionCode,
    });
    history.push(`/SoftwareUpdate/${id}`);
  });

  const handleCancelClicked = () => {
    history.push(`/SoftwareUpdate/${id}`);
  };

  const handleSitesChanged = (ids) => {
    if (!_.isEqual(ids, selectedSites)) {
      setWasSelectedSitesChanged(true);
      setSelectedSites(ids);
    }
  };

  const renderTimeTabs = () => {
    if (timeTab == null) return null;
    return (
      <Tabs
        variant='app'
        defaultSelection={timeTab}
        onSelect={(key) => {
          setAsapSelected(key === 'asap');
          setOnHoldSelected(key === 'onhold');
        }}
        disabled={!timeSectionEnabled}
      >
        <Tab value='custom' title='Scheduled'>
          <Form.Group>
            <Form.Label required>Update Date &amp; Time</Form.Label>
            <Inputs.DateTime
              required
              placeholder='Please select update date and time'
              description='Update Date & Time'
              help='Site Local Time'
              value={distributeDateTime}
              onChange={setDistributeDateTime}
              disabled={!timeSectionEnabled}
            />
          </Form.Group>
        </Tab>
        <Tab value='asap' title='ASAP'>
          <div>This update will begin as soon as possible.</div>
        </Tab>
        <Tab value='onhold' title='On Hold'>
          <div>This update will be on hold. It will not execute until manually removed from the hold.</div>
        </Tab>
      </Tabs>
    );
  };

  const renderAdditionalOptions = () => {
    switch (selectedType) {
      case 3:
        return (
          <Form.Group>
            <Form.Label>QuickMenu Options</Form.Label>
            <Inputs.Checkbox
              label='Run QM database repair after update'
              checked={distributeOptionCode === 'repair'}
              disabled={!optionSectionEnabled}
              onValueChange={(v) => setDistributeOptionCode(v ? 'repair' : null)}
              block
            />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  if (dataNotFound) {
    return <ErrorItemNotFound />;
  }

  if (!selectedType) {
    //Loading View
    return (
      <>
        <Heading />

        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Panel>
              <Panel.SkeletonHeader width={200} />
              <Panel.SkeletonBody rows={6} rowHeight={54} rowMargin={22} />
              <Panel.SkeletonFooter width={250} />
            </Panel>
          </div>
        </div>
      </>
    );
  }

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
            <Inputs.Button variant='success' onClick={handleSaveUpdateClicked} iconClass='icons8-save' label='Save' />
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Form ref={setFormRef} noValidate>
              <Panel>
                <Panel.Header>Edit Update</Panel.Header>
                <Panel.Body>
                  <Form.Group>
                    <Form.Label required>Description</Form.Label>
                    <Inputs.Text
                      required
                      placeholder='Please enter a description of the update'
                      description='Description'
                      value={softwareUpdateName}
                      onValueChange={setSoftwareUpdateName}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Software</Form.Label>
                    <Inputs.Select
                      required
                      placeholder='Please select software'
                      description='Software'
                      value={selectedType}
                      disabled={true}
                    >
                      {softwareTypes
                        .filter((item) => item.isUpdatable)
                        .map((item) => (
                          <Inputs.Option value={item.id} label={item.name} key={item.id} />
                        ))}
                    </Inputs.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Version</Form.Label>
                    <Inputs.Select
                      required
                      placeholder='Please select version'
                      description='Version'
                      disabled={!softwareSectionEnabled}
                      value={softwareVersionId}
                      onValueChange={(v) => setSoftwareVersionId(parseInt(v))}
                    >
                      {selectedType && (softwareTypeVersionsMap[selectedType] || []).map((item) => (
                        <Inputs.Option value={item.id} key={item.id}>
                          {item.name} {item.buildDate ? '(' + moment(item.buildDate).format('L') + ')' : ''}
                        </Inputs.Option>
                      ))}
                    </Inputs.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Sites</Form.Label>
                    <SiteSelectorFormControl
                      required
                      disabled={!sitesSectionEnabled}
                      sitesSelected={selectedSites}
                      onChange={handleSitesChanged}
                      areaId={appState.area.id}
                      selectableFn={SoftwareUpdateHelper.siteFilterFn}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Schedule</Form.Label>
                    {renderTimeTabs()}
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Throttling</Form.Label>
                    <div>
                      <span className='mr-2'>Run an update at</span>
                      <Inputs.Integer
                        required
                        min='1'
                        max='999'
                        value={updateRateVolume}
                        onValueChange={(val) => setUpdateRateVolume(val)}
                        style={styles.numberInput}
                        disabled={!timeSectionEnabled}
                      />
                      <span className='mx-2'>sites every</span>
                      <Inputs.Integer
                        required
                        min='1'
                        max='120'
                        value={updateRateFrequency}
                        onValueChange={(val) => setUpdateRateFrequency(val)}
                        style={styles.numberInput}
                        disabled={!timeSectionEnabled}
                      />
                      <span className='ml-2'>minutes</span>
                    </div>
                  </Form.Group>
                  {renderAdditionalOptions()}
                </Panel.Body>
                <Panel.Footer>
                  {renderFormValidationMessage()}
                  <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
                  <Inputs.Button variant='success' onClick={handleSaveUpdateClicked} iconClass='icons8-save' label='Save' />
                </Panel.Footer>
              </Panel>
            </Form>
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}
